import merge from 'Utils/lodash.merge';
import set from 'Utils/lodash.set';
import get from 'Utils/lodash.get';
import auth from 'Auth';
import { SERVER_AUTH_ERROR_MESSAGE } from 'Config/constans';
import HttpClientError from './HttpClientError';

const defaultOptions = {
  headers: {
    'Content-Type': 'application/json;charset=UTF-8',
  },
};

export default class HttpClient {
  constructor(baseURL = '', options = {}) {
    this.baseURL = baseURL;
    this.requestOptions = {};
    merge(this.requestOptions, defaultOptions, options);
  }

  _getOptions(options, requestData) {
    const opt = {};
    const { token } = auth;
    if (token) {
      set(opt, 'headers.Authorization', token);
    }
    merge(opt, this.requestOptions, options);
    // set body to options
    opt.body = this._getBody(requestData, opt.method);
    return this._normalizeOptions(opt);
  }

  _getRequestURL(path) {
    let url = this.baseURL;
    if (!this.baseURL.endsWith('/') && path && !String(path).startsWith('/')) {
      url += '/';
    }
    url += path;
    return url;
  }

  _getBody(data, method) {
    if (['GET', 'HEAD'].includes(method)) return undefined;
    if (data instanceof FormData) return data;
    return JSON.stringify(data);
  }

  _normalizeOptions(options) {
    const opt = options;
    opt.method = opt.method.toUpperCase();
    // Remove Authorization header if null
    if (get(opt, 'headers.Authorization') === null) delete opt.headers.Authorization;
    // If body FormData, delete header Content-Type to make fetch handle formdata
    if (opt.body instanceof FormData) delete opt.headers['Content-Type'];
    return opt;
  }

  _onSuccess(result) {
    return result;
  }

  async _onError(error) {
    const { default: store } = await import('Store');
    if ([403, 401].includes(get(error, 'response.status')) && store.getters['login/isLoggedIn']) {
      // eslint-disable-next-line no-console
      console.log('==debug== error on http request: ', error);
      // logout user
      store.dispatch('login/logout', true);
      store.dispatch('toaster/setMessage', {
        text: SERVER_AUTH_ERROR_MESSAGE,
        options: {
          timeout: 10000,
          type: 'error',
        },
      });
    }
  }

  _parseJSON(response) {
    return response.text()
      .then((text) => {
        let result = {};
        try {
          result = JSON.parse(text);
        } catch (ignore) { } // eslint-disable-line
        Object.assign(response, {
          data: result,
        });
        return response;
      });
  }

  _checkStatus(response) {
    return new Promise((resolve) => {
      if (response.ok) {
        this._onSuccess(response);
        return resolve(response);
      }
      throw new HttpClientError(response);
    });
  }

  fetch(url, options) {
    return fetch(url, options);
  }

  request(method = 'GET', path = '', requestData = {}, overrideOptions = {}) {
    const options = this._getOptions({ ...overrideOptions, method }, requestData);
    return this.fetch(this._getRequestURL(path), options)
      .then(this._parseJSON.bind(this))
      .then(this._checkStatus.bind(this))
      .catch((e) => {
        this._onError(e);
        throw e;
      });
  }

  get(path, options) {
    return this.request('GET', path, null, options);
  }

  post(...args) {
    return this.request('POST', ...args);
  }

  patch(...args) {
    return this.request('PATCH', ...args);
  }

  put(...args) {
    return this.request('PUT', ...args);
  }

  delete(...args) {
    return this.request('DELETE', ...args);
  }

  options(...args) {
    return this.request('OPTIONS', ...args);
  }

  head(...args) {
    return this.request('HEAD', ...args);
  }
}
